import { SummaryTable } from '../SummaryTable.tsx';
import { SummaryTableBoldDivider } from '../SummaryTableBoldDivider.tsx';
import { Tkv2PairedCostSummaryInterfaceV2 } from '../types.ts';
import { CostApproachSummaryIndicatedValue } from './CostApproachSummaryIndicatedValue.tsx';
import { CostApproachSummaryManufacturedDepreciationRow } from './CostApproachSummaryManufacturedDepreciationRow.tsx';
import { CostApproachSummaryRollupRow } from './CostApproachSummaryRollupRow.tsx';
import { CostApproachSummaryRow } from './CostApproachSummaryRow.tsx';
import { CostApproachSummarySectionsRow } from './CostApproachSummarySectionsRow.tsx';
import { CostApproachSummarySiteValueRow } from './CostApproachSummarySiteValueRow.tsx';
import { CostApproachSummaryTableHeader } from './CostApproachSummaryTableHeader.tsx';
import { CostApproachSummaryTextRow } from './CostApproachSummaryTextRow.tsx';

export const CostApproachManufacturedSummaryTable = (props: Tkv2PairedCostSummaryInterfaceV2) => {
  const {
    results: { subject },
  } = props;

  const sectionsToNumber = Number(subject?.features.mainDwelling?.sections);
  const numSections = Number.isNaN(sectionsToNumber) ? 1 : sectionsToNumber;

  return (
    <SummaryTable {...props}>
      <CostApproachSummaryTableHeader />
      <CostApproachSummarySiteValueRow siteValue={subject?.totals?.costApproach?.site} {...props} />
      <CostApproachSummarySectionsRow
        label="Section One"
        row={subject?.totals?.costApproach?.dwelling}
        onlyShowCostPerSqft={false}
        showBlankData={false}
        {...props}
      />
      <CostApproachSummarySectionsRow
        label="Section Two"
        row={subject?.totals?.costApproach?.dwelling}
        onlyShowCostPerSqft
        showBlankData={numSections < 2}
        {...props}
      />
      <CostApproachSummarySectionsRow
        label="Section Three"
        row={subject?.totals?.costApproach?.dwelling}
        onlyShowCostPerSqft
        showBlankData={numSections < 3}
        {...props}
      />
      <CostApproachSummarySectionsRow
        label="Section Four"
        row={subject?.totals?.costApproach?.dwelling}
        onlyShowCostPerSqft
        showBlankData={numSections < 4}
        {...props}
      />
      <CostApproachSummaryRow
        label="Basement"
        row={subject?.totals?.costApproach?.basement}
        {...props}
      />
      <CostApproachSummaryRow
        label="Additional Structures"
        row={subject?.totals?.costApproach?.additionalStructures}
        {...props}
      />
      <CostApproachSummaryRow
        label="Garage/Carport"
        row={subject?.totals?.costApproach?.garageCarport}
        {...props}
      />
      <SummaryTableBoldDivider />

      <CostApproachSummaryRollupRow
        {...props}
        label="Sub-total"
        value={
          subject?.totals?.improvements.new.cost
            ? `= ${subject?.totals?.improvements.new.cost?.toString(true)}`
            : ''
        }
      />
      <CostApproachSummaryTextRow
        {...props}
        label="Cost Multiplier (if applicable)"
        value="Not applicable"
        tooltip="The cost multiplier line is specific to an appraiser using the NADA cost guide for manufactured housing. Since Toolkit uses Dwelling Cost it is not applicable."
      />
      <SummaryTableBoldDivider />
      <CostApproachSummaryRollupRow
        {...props}
        label="Modified Sub-total"
        value={
          subject?.totals?.improvements.new.cost
            ? `= ${subject?.totals?.improvements.new.cost?.toString(true)}`
            : ''
        }
      />
      <CostApproachSummaryManufacturedDepreciationRow
        {...props}
        label="Physical Depreciation or Condition Modifier"
        depreciation={subject?.totals?.costApproach?.depreciation.physical?.abs()}
      />
      <CostApproachSummaryManufacturedDepreciationRow
        {...props}
        label="Functional Obsolescence"
        depreciation={subject?.totals?.costApproach?.depreciation.functional?.abs()}
      />
      <CostApproachSummaryManufacturedDepreciationRow
        {...props}
        label="External Depreciation or State Location Modifier"
        depreciation={subject?.totals?.costApproach?.depreciation.external?.abs()}
      />
      <CostApproachSummaryTextRow
        {...props}
        label="Delivery, Installation and Setup"
        value="Included in costs"
      />
      <CostApproachSummaryTextRow
        {...props}
        label="Other Depreciated Site Improvements"
        value="Included in site value"
      />
      <CostApproachSummaryTextRow
        {...props}
        label="Market Value of Subject Site"
        value={
          subject?.totals?.costApproach?.site
            ? `= ${subject.totals.costApproach.site.toString(true)}`
            : ''
        }
        boldValue
      />
      <CostApproachSummaryIndicatedValue
        indicatedValue={subject?.totals?.costApproach?.indicatedValue}
        {...props}
      />
    </SummaryTable>
  );
};
